import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../gpu';
import { ATMOSPHERE_COMMON, SKYVIEW_FRAG, SKYVIEW_LOOKUP, TRANSMITTANCE_FRAG } from './atmosphereGlsl';
import { CLOUDS_GLSL, NOISE3D_FRAG } from './cloudGlsl';
import { ATMO_GLSL, atmoUniforms } from '../atmosphere';
import { skyIrradiance, transmittance } from './atmosphereCpu';
import type { WeatherParams } from '../../sim/weather';

/** Direct sun illuminance above the atmosphere, scene units (~PI -> white). */
export const SUN_E = 3.4;
/** Moonlight: far brighter than reality so night stays readable. */
export const MOON_E = SUN_E / 260;

export interface SkyInput {
  camera: THREE.PerspectiveCamera;
  sunDir: THREE.Vector3;
  moonDir: THREE.Vector3;
  weather: WeatherParams;
  windX: number;
  windZ: number;
  windSpeed: number;
  dt: number;
  time: number;
  flash: THREE.Vector4;
}

export interface SkyQuality {
  cloudSteps: number;
  cubeSteps: number;
  skyScale: number;
}

/** Lighting derived from the sky each frame, consumed by the scene. */
export interface SkyLighting {
  keyDir: THREE.Vector3;
  /** Direct irradiance at sea level (transmittance and overcast applied). */
  keyColor: THREE.Color;
  keyIsSun: boolean;
  /** Sky irradiance on a horizontal surface. */
  ambient: THREE.Color;
  /** 0 day .. 1 full night. */
  night: number;
  sunTransmittance: THREE.Color;
}

const SKY_FRAG = /* glsl */ `
${ATMOSPHERE_COMMON}
${SKYVIEW_LOOKUP}
uniform sampler2D uTrans;
uniform sampler2D uSkyView;
uniform float uSunAz;
uniform float uSkyScale;
uniform mat4 uInvViewProj;
uniform vec3 uCamPos;
uniform sampler2D uHistory;
uniform mat4 uPrevViewProjRot;
uniform float uHistoryWeight;
uniform vec2 uRes;
${CLOUDS_GLSL}
in vec2 vUv;

void main() {
  vec4 clip = vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec4 wpos = uInvViewProj * clip;
  vec3 dir = normalize(wpos.xyz / wpos.w - uCamPos);
  vec3 sky = skyViewLookup(uSkyView, dir, uSunAz) * uSkyScale;
  if (dir.y < -0.03) {
    // Below the horizon: a dim sea for reflections / IBL.
    vec3 hz = skyViewLookup(uSkyView, normalize(vec3(dir.x, 0.02, dir.z)), uSunAz) * uSkyScale;
    gl_FragColor = vec4(hz * 0.09 + uAmbTop * 0.012, 1.0);
    return;
  }
  float hit;
  vec4 cl = marchClouds(uCamPos, dir, gl_FragCoord.xy, hit);
  // Aerial perspective between camera and clouds.
  float air = exp(-hit / (45000.0 / max(uMieScale, 0.3)));
  vec3 col = sky * cl.a + cl.rgb * air + sky * (1.0 - cl.a) * (1.0 - air);
  vec4 cur = vec4(col, cl.a);
  if (uHistoryWeight > 0.0) {
    vec4 pc = uPrevViewProjRot * vec4(dir, 0.0);
    vec2 puv = pc.xy / pc.w * 0.5 + 0.5;
    if (pc.w > 0.0 && all(greaterThan(puv, vec2(0.002))) && all(lessThan(puv, vec2(0.998)))) {
      vec4 h = texture(uHistory, puv);
      cur = mix(cur, h, uHistoryWeight);
    }
  }
  gl_FragColor = cur;
}
`;

const SHADOW_FRAG = /* glsl */ `
${ATMOSPHERE_COMMON}
uniform sampler2D uTrans;
${CLOUDS_GLSL}
uniform vec3 uCenter;
uniform float uSize;
in vec2 vUv;
void main() {
  vec2 xz = uCenter.xz + (vUv - 0.5) * uSize;
  vec3 L = uKeyDir;
  float ly = max(L.y, 0.08);
  float base = uCloud.z, thick = uCloud.w;
  float t0 = base / ly, t1 = min((base + thick) / ly, t0 + 7000.0);
  float od = 0.0;
  float dt = (t1 - t0) / 8.0;
  for (int i = 0; i < 8; i++) {
    vec3 p = vec3(xz.x, 0.0, xz.y) + L * (t0 + (float(i) + 0.5) * dt);
    float h = (p.y - base) / thick;
    od += cloudDensity(p, clamp(h, 0.0, 1.0), false) * dt;
  }
  gl_FragColor = vec4(max(exp(-od * 0.02), 0.12), 0.0, 0.0, 1.0);
}
`;

const BACKGROUND_VERT = /* glsl */ `
out vec3 vDir;
void main() {
  vec4 v = inverse(projectionMatrix) * vec4(position.xy, 1.0, 1.0);
  vDir = (vec4(v.xyz / v.w, 0.0) * viewMatrix).xyz;
  gl_Position = vec4(position.xy, 1.0, 1.0);
}
`;

const BACKGROUND_FRAG = /* glsl */ `
${ATMOSPHERE_COMMON}
${ATMO_GLSL}
uniform sampler2D uSkyTex;
uniform sampler2D uTrans;
uniform vec2 uRes;
uniform vec3 uSunB;
uniform vec3 uMoonB;
uniform float uSunI;
uniform float uMoonI;
uniform float uStars;
uniform float uCamHkm;
uniform float uTimeB;
in vec3 vDir;

float h21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y);
}

vec3 stars(vec3 d) {
  vec3 a = abs(d);
  vec2 uv; float face;
  if (a.x > a.y && a.x > a.z) { uv = d.yz / a.x; face = sign(d.x); }
  else if (a.y > a.z) { uv = d.xz / a.y; face = 2.0 + sign(d.y); }
  else { uv = d.xy / a.z; face = 4.0 + sign(d.z); }
  vec3 col = vec3(0.0);
  for (int layer = 0; layer < 2; layer++) {
    float scale = layer == 0 ? 180.0 : 420.0;
    vec2 g = uv * scale + face * 17.0 + float(layer) * 91.0;
    vec2 id = floor(g);
    float r = h21(id);
    float thresh = layer == 0 ? 0.965 : 0.93;
    if (r > thresh) {
      vec2 c = vec2(h21(id + 3.1), h21(id + 7.7)) * 0.7 + 0.15;
      float dist = length(fract(g) - c);
      float mag = pow((r - thresh) / (1.0 - thresh), layer == 0 ? 3.0 : 6.0);
      float tw = 0.75 + 0.25 * sin(uTimeB * (3.0 + r * 9.0) + r * 40.0);
      float temp = h21(id + 1.3);
      vec3 tint = mix(vec3(1.0, 0.78, 0.6), vec3(0.7, 0.82, 1.0), temp);
      col += tint * mag * tw * exp(-dist * dist * 900.0) * (layer == 0 ? 6.0 : 2.0);
    }
  }
  // A faint Milky Way band.
  vec3 gp = normalize(vec3(0.35, 0.55, -0.76));
  float band = exp(-pow(dot(d, gp) * 5.0, 2.0));
  float n = vnoise(uv * 9.0 + face) * 0.6 + vnoise(uv * 23.0 - face) * 0.4;
  col += vec3(0.55, 0.6, 0.75) * band * n * 0.05;
  return col;
}

void main() {
  vec3 dir = normalize(vDir);
  vec2 suv = gl_FragCoord.xy / uRes;
  vec4 sky = texture(uSkyTex, suv);
  vec3 col = sky.rgb;
  float cloudT = sky.a;
  if (dir.y < 0.0) {
    // Sea beyond the ocean mesh, lost in the haze.
    vec3 far = uSkyAmbient * 0.02;
    gl_FragColor = vec4(applyAtmosphere(far, dir * 40000.0), 1.0);
    return;
  }
  vec3 tView = texture(uTrans, transmittanceUV(uCamHkm, dir.y)).rgb;
  // Sun disk with limb darkening.
  float sunR = 0.0072;
  float cs = dot(dir, uSunB);
  if (cs > cos(sunR)) {
    float x = acos(clamp(cs, -1.0, 1.0)) / sunR;
    float limb = 1.0 - 0.6 * (1.0 - sqrt(max(0.0, 1.0 - x * x)));
    col += uSunI / (PI * sunR * sunR) * tView * limb * cloudT;
  }
  // Moon: a lit sphere with maria.
  float moonR = 0.0068;
  float cm = dot(dir, uMoonB);
  if (cm > cos(moonR)) {
    vec3 up0 = abs(uMoonB.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 mx = normalize(cross(up0, uMoonB));
    vec3 my = cross(uMoonB, mx);
    vec2 q = vec2(dot(dir - uMoonB, mx), dot(dir - uMoonB, my)) / moonR;
    float z = sqrt(max(0.0, 1.0 - dot(q, q)));
    vec3 n = normalize(q.x * mx + q.y * my - z * uMoonB);
    float lit = max(0.0, dot(n, uSunB));
    float maria = 0.62 + 0.38 * smoothstep(0.35, 0.7, vnoise(q * 3.0 + 4.0) * 0.7 + vnoise(q * 9.0) * 0.3);
    float edge = (1.0 - smoothstep(0.94, 1.0, length(q)));
    col += uMoonI * 1.6 / (PI * moonR * moonR) * (0.06 + lit) * maria * tView * edge * cloudT;
  }
  col += stars(dir) * uStars * tView * cloudT * 0.02;
  // Sea fog swallows the sky near the horizon.
  float fogOd = atmoLayerDepth(uFog, uFogHeight, cameraPosition.y, max(dir.y, 0.0), 60000.0);
  float T = exp(-fogOd);
  vec3 fogCol = uSkyAmbient * 0.55 + uSunColor * (atmoPhaseHG(dot(dir, uSunDir), 0.72) * 0.9 + 0.02) * 0.5;
  col = col * T + fogCol * (1.0 - T);
  gl_FragColor = vec4(col, 1.0);
}
`;

export class Sky {
  readonly trans = makeTarget(256, 64, { wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping });
  readonly skyView = makeTarget(192, 108, { wrapS: THREE.RepeatWrapping, wrapT: THREE.ClampToEdgeWrapping });
  readonly noise: THREE.Data3DTexture;
  private skyRT: THREE.WebGLRenderTarget[];
  private skyIndex = 0;
  readonly cube: THREE.WebGLCubeRenderTarget;
  private readonly cubeCam: THREE.CubeCamera;
  private cubeFace = 0;
  private pmrem: THREE.PMREMGenerator;
  private pmremRT: THREE.WebGLRenderTarget | null = null;
  private pmremTimer = 0;
  readonly shadow = makeTarget(256, 256, { type: THREE.UnsignedByteType });
  private shadowTimer = 0;
  readonly shadowSize = 16000;
  readonly shadowCenter = new THREE.Vector3();

  private readonly transPass = new FullscreenPass(TRANSMITTANCE_FRAG, { uMieScale: { value: 1 } });
  private readonly skyViewPass: FullscreenPass;
  private readonly skyPass: FullscreenPass;
  private readonly shadowPass: FullscreenPass;
  readonly background: THREE.Mesh;
  private readonly bgMat: THREE.ShaderMaterial;

  private readonly shared: Record<string, THREE.IUniform>;
  private cloudOffset = new THREE.Vector3();
  private frame = 0;
  private readonly prevViewProjRot = new THREE.Matrix4();
  private hasHistory = false;
  private readonly lighting: SkyLighting = {
    keyDir: new THREE.Vector3(0, 1, 0),
    keyColor: new THREE.Color(),
    keyIsSun: true,
    ambient: new THREE.Color(),
    night: 0,
    sunTransmittance: new THREE.Color(),
  };
  private lastMie = -1;
  quality: SkyQuality = { cloudSteps: 44, cubeSteps: 18, skyScale: 0.5 };

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    this.noise = this.bakeNoise();

    this.skyRT = [0, 1].map(() => makeTarget(4, 4));
    this.cube = new THREE.WebGLCubeRenderTarget(128, {
      type: THREE.HalfFloatType,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.cubeCam = new THREE.CubeCamera(1, 10, this.cube);
    this.cubeCam.coordinateSystem = renderer.coordinateSystem;
    this.cubeCam.updateCoordinateSystem();
    this.pmrem = new THREE.PMREMGenerator(renderer);

    this.shared = {
      uMieScale: { value: 1 },
      uTrans: { value: this.trans.texture },
      uSkyView: { value: this.skyView.texture },
      uNoise: { value: this.noise },
      uSunAz: { value: 0 },
      uSkyScale: { value: SUN_E },
      uCloud: { value: new THREE.Vector4(0.4, 1, 1200, 1500) },
      uStratus: { value: 0 },
      uCloudOffset: { value: this.cloudOffset },
      uCloudSteps: { value: 40 },
      uKeyDir: { value: new THREE.Vector3(0, 1, 0) },
      uKeyColor: { value: new THREE.Color(1, 1, 1) },
      uAmbTop: { value: new THREE.Color() },
      uAmbBottom: { value: new THREE.Color() },
      uFlashPos: { value: new THREE.Vector4(0, 0, 0, 0) },
      uFrame: { value: 0 },
    };
    this.skyViewPass = new FullscreenPass(SKYVIEW_FRAG, {
      uMieScale: this.shared.uMieScale!,
      uTrans: this.shared.uTrans!,
      uCamH: { value: 0.01 },
      uSunEl: { value: 0.5 },
      uMoonLocal: { value: new THREE.Vector3(0, -1, 0) },
      uMoonI: { value: MOON_E / SUN_E },
    });
    this.skyPass = new FullscreenPass(SKY_FRAG, {
      ...this.shared,
      uInvViewProj: { value: new THREE.Matrix4() },
      uCamPos: { value: new THREE.Vector3() },
      uHistory: { value: null },
      uPrevViewProjRot: { value: new THREE.Matrix4() },
      uHistoryWeight: { value: 0 },
      uRes: { value: new THREE.Vector2() },
    });
    this.shadowPass = new FullscreenPass(SHADOW_FRAG, {
      ...this.shared,
      uCenter: { value: this.shadowCenter },
      uSize: { value: this.shadowSize },
    });

    this.bgMat = new THREE.ShaderMaterial({
      vertexShader: BACKGROUND_VERT,
      fragmentShader: BACKGROUND_FRAG,
      uniforms: {
        ...atmoUniforms,
        uMieScale: this.shared.uMieScale!,
        uTrans: this.shared.uTrans!,
        uSkyTex: { value: null },
        uRes: { value: new THREE.Vector2(1, 1) },
        uSunB: { value: new THREE.Vector3() },
        uMoonB: { value: new THREE.Vector3() },
        uSunI: { value: SUN_E },
        uMoonI: { value: MOON_E },
        uStars: { value: 0 },
        uCamHkm: { value: 0.01 },
        uTimeB: { value: 0 },
      },
      depthWrite: false,
      depthTest: true,
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    this.background = new THREE.Mesh(g, this.bgMat);
    this.background.frustumCulled = false;
    this.background.renderOrder = 1000;

    this.shadow.texture.wrapS = this.shadow.texture.wrapT = THREE.ClampToEdgeWrapping;
    atmoUniforms.uCloudShadow.value = this.shadow.texture;
    atmoUniforms.uSkyCube.value = this.cube.texture;
  }

  /**
   * Bake the tileable cloud noise one slice at a time into a 2D target and
   * read it back into a 3D texture (a one-off ~8 MB readback at start-up).
   */
  private bakeNoise(): THREE.Data3DTexture {
    const N = 128;
    const pass = new FullscreenPass(NOISE3D_FRAG, { uZ: { value: 0 } });
    const slice = new THREE.WebGLRenderTarget(N, N, { type: THREE.UnsignedByteType, depthBuffer: false });
    const data = new Uint8Array(N * N * N * 4);
    for (let z = 0; z < N; z++) {
      pass.uniforms.uZ!.value = (z + 0.5) / N;
      pass.render(this.renderer, slice);
      this.renderer.readRenderTargetPixels(slice, 0, 0, N, N, data.subarray(z * N * N * 4, (z + 1) * N * N * 4));
    }
    pass.dispose();
    slice.dispose();
    this.renderer.setRenderTarget(null);
    const tex = new THREE.Data3DTexture(data, N, N, N);
    tex.format = THREE.RGBAFormat;
    tex.type = THREE.UnsignedByteType;
    tex.wrapS = tex.wrapT = tex.wrapR = THREE.RepeatWrapping;
    tex.minFilter = tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.unpackAlignment = 1;
    tex.needsUpdate = true;
    return tex;
  }

  resize(w: number, h: number): void {
    const s = this.quality.skyScale;
    const sw = Math.max(16, Math.round(w * s)), sh = Math.max(16, Math.round(h * s));
    for (const rt of this.skyRT) rt.setSize(sw, sh);
    this.skyPass.uniforms.uRes!.value.set(sw, sh);
    this.bgMat.uniforms.uRes!.value.set(w, h);
    this.hasHistory = false;
  }

  /** A camera cut: drop the clouds' reprojection history. */
  cut(): void {
    this.hasHistory = false;
  }

  /** Environment map for PBR materials (prefiltered). */
  get environment(): THREE.Texture | null {
    return this.pmremRT?.texture ?? null;
  }

  /**
   * Transmittance and sky irradiance at sea level for the sun and the moon,
   * integrated on the CPU; they only change as the lights climb or the haze
   * thickens, so they're worked out again only then.
   */
  private readonly atmo = { sunY: NaN, moonY: NaN, mie: NaN, t: [0, 0, 0], tm: [0, 0, 0], amb: [0, 0, 0], ambM: [0, 0, 0] };

  update(input: SkyInput): SkyLighting {
    const r = this.renderer;
    const w = input.weather;
    const L = this.lighting;
    this.frame++;
    const mie = 1 + w.haze * 5 + w.fog * 3;
    this.shared.uMieScale!.value = mie;
    if (Math.abs(mie - this.lastMie) > 0.02) {
      this.lastMie = mie;
      this.transPass.uniforms.uMieScale!.value = mie;
      this.transPass.render(r, this.trans);
    }

    // --- light directions & colours
    const sun = input.sunDir;
    const moon = input.moonDir;
    const sunEl = Math.asin(THREE.MathUtils.clamp(sun.y, -1, 1));
    const useSun = sunEl > -0.1;
    L.keyIsSun = useSun;
    L.keyDir.copy(useSun ? sun : moon);
    if (L.keyDir.y < 0.02) L.keyDir.y = 0.02;
    L.keyDir.normalize();
    L.night = THREE.MathUtils.clamp((-sunEl - 0.02) / 0.2, 0, 1);

    const a = this.atmo;
    // Written as !(<=) so the NaN start counts as changed: a plain `>` is
    // false against NaN and would leave the lights black for good.
    const hazeChanged = !(Math.abs(mie - a.mie) <= 1e-3);
    if (hazeChanged || !(Math.abs(sun.y - a.sunY) <= 2e-4)) {
      a.sunY = sun.y;
      transmittance(0.01, sun.y, mie, a.t);
      skyIrradiance(sun.y, mie, a.amb);
    }
    if (hazeChanged || !(Math.abs(moon.y - a.moonY) <= 2e-4)) {
      a.moonY = moon.y;
      transmittance(0.01, moon.y, mie, a.tm);
      skyIrradiance(moon.y, mie, a.ambM);
    }
    if (hazeChanged) a.mie = mie;
    const t = a.t, tm = a.tm;
    L.sunTransmittance.setRGB(t[0]!, t[1]!, t[2]!);
    const overcast = Math.max(w.stratus, Math.pow(w.cloudCover, 3));
    const directDim = 1 - 0.82 * overcast;
    if (useSun) L.keyColor.setRGB(t[0]! * SUN_E, t[1]! * SUN_E, t[2]! * SUN_E).multiplyScalar(directDim);
    else L.keyColor.setRGB(tm[0]! * MOON_E, tm[1]! * MOON_E * 1.02, tm[2]! * MOON_E * 1.08).multiplyScalar(directDim);
    // Fade the key light through the switch-over in deep twilight.
    const twilight = THREE.MathUtils.smoothstep(Math.abs(sunEl + 0.1), 0, 0.05);
    L.keyColor.multiplyScalar(twilight);

    const amb = a.amb, ambM = a.ambM;
    L.ambient.setRGB(amb[0]! * SUN_E + ambM[0]! * MOON_E, amb[1]! * SUN_E + ambM[1]! * MOON_E, amb[2]! * SUN_E + ambM[2]! * MOON_E);
    // Under cloud the sky light greys out; some of the blocked sun comes
    // through as diffuse light.
    const lum = L.ambient.r * 0.2126 + L.ambient.g * 0.7152 + L.ambient.b * 0.0722;
    const diffuseSun = (useSun ? SUN_E * Math.max(sun.y, 0) * (t[1]!) : 0) * 0.28 * overcast;
    const grey = new THREE.Color(lum * 0.9 + diffuseSun, lum * 0.93 + diffuseSun, lum + diffuseSun * 1.05);
    L.ambient.lerp(grey, overcast * 0.85);
    L.ambient.multiplyScalar(1 - 0.35 * w.rain);
    // Starlight / airglow floor.
    L.ambient.r += 0.0009;
    L.ambient.g += 0.0011;
    L.ambient.b += 0.0018;

    // --- sky-view LUT
    const sunAz = Math.atan2(sun.z, sun.x);
    const moonAz = Math.atan2(moon.z, moon.x) - sunAz;
    const moonEl = Math.asin(THREE.MathUtils.clamp(moon.y, -1, 1));
    const svp = this.skyViewPass.uniforms;
    svp.uCamH!.value = Math.max(0.002, input.camera.position.y * 0.001);
    svp.uSunEl!.value = sunEl;
    svp.uMoonLocal!.value.set(Math.cos(moonEl) * Math.cos(moonAz), Math.sin(moonEl), Math.cos(moonEl) * Math.sin(moonAz));
    this.skyViewPass.render(r, this.skyView);

    // --- clouds
    const sh = this.shared;
    sh.uSunAz!.value = sunAz;
    // Wind aloft is roughly twice the surface wind.
    this.cloudOffset.x -= input.windX * input.windSpeed * 2.2 * input.dt;
    this.cloudOffset.z -= input.windZ * input.windSpeed * 2.2 * input.dt;
    this.cloudOffset.y += input.dt * 1.5;
    sh.uCloud!.value.set(w.cloudCover, w.cloudDensity, w.cloudBase, w.cloudThickness);
    sh.uStratus!.value = w.stratus;
    sh.uKeyDir!.value.copy(L.keyDir);
    const keyE = useSun ? SUN_E : MOON_E;
    sh.uKeyColor!.value.setRGB(keyE, keyE, keyE).multiplyScalar(twilight);
    const skyTop = new THREE.Color(amb[0]! * SUN_E + ambM[0]! * MOON_E, amb[1]! * SUN_E + ambM[1]! * MOON_E, amb[2]! * SUN_E + ambM[2]! * MOON_E);
    // Skylight on the cloud tops; sea-reflected and bounced light underneath.
    sh.uAmbTop!.value.copy(skyTop).multiplyScalar(0.28);
    sh.uAmbBottom!.value.copy(L.ambient).multiplyScalar(0.07).add(new THREE.Color().copy(L.keyColor).multiplyScalar(0.012));
    sh.uFlashPos!.value.copy(input.flash);
    sh.uFrame!.value = this.frame % 64;

    const cam = input.camera;
    const invVP = new THREE.Matrix4().multiplyMatrices(cam.matrixWorld, cam.projectionMatrixInverse);
    const sp = this.skyPass.uniforms;
    sp.uInvViewProj!.value.copy(invVP);
    sp.uCamPos!.value.copy(cam.position);
    sh.uCloudSteps!.value = this.quality.cloudSteps;
    const prev = this.skyRT[this.skyIndex]!;
    this.skyIndex ^= 1;
    const cur = this.skyRT[this.skyIndex]!;
    sp.uHistory!.value = prev.texture;
    sp.uPrevViewProjRot!.value.copy(this.prevViewProjRot);
    sp.uHistoryWeight!.value = this.hasHistory ? (input.flash.w > 0.05 ? 0.3 : 0.86) : 0;
    this.skyPass.render(r, cur);
    this.hasHistory = true;
    const viewRot = new THREE.Matrix4().copy(cam.matrixWorldInverse).setPosition(0, 0, 0);
    this.prevViewProjRot.multiplyMatrices(cam.projectionMatrix, viewRot);
    this.bgMat.uniforms.uSkyTex!.value = cur.texture;

    // --- environment cube: two faces per frame
    this.cubeCam.position.copy(cam.position);
    this.cubeCam.updateMatrixWorld(true);
    sh.uCloudSteps!.value = this.quality.cubeSteps;
    sp.uHistoryWeight!.value = 0;
    for (let k = 0; k < 2; k++) {
      const face = this.cubeFace;
      this.cubeFace = (this.cubeFace + 1) % 6;
      const fc = this.cubeCam.children[face] as THREE.PerspectiveCamera;
      fc.updateMatrixWorld(true);
      sp.uInvViewProj!.value.multiplyMatrices(fc.matrixWorld, fc.projectionMatrixInverse);
      this.skyPass.render(r, this.cube as unknown as THREE.WebGLRenderTarget, face);
    }
    this.pmremTimer -= input.dt;
    if (this.pmremTimer <= 0 || !this.pmremRT) {
      this.pmremTimer = 0.5;
      this.pmremRT = this.pmrem.fromCubemap(this.cube.texture, this.pmremRT ?? undefined);
    }

    // --- cloud shadow map
    this.shadowTimer -= input.dt;
    const moved = this.shadowCenter.distanceTo(cam.position) > this.shadowSize * 0.2;
    if (this.shadowTimer <= 0 || moved) {
      this.shadowTimer = 0.1;
      if (moved) this.shadowCenter.set(Math.round(cam.position.x / 500) * 500, 0, Math.round(cam.position.z / 500) * 500);
      this.shadowPass.render(r, this.shadow);
    }
    atmoUniforms.uCloudShadowRect.value.set(this.shadowCenter.x, this.shadowCenter.z, 1 / this.shadowSize, 1);

    // --- background uniforms
    const bg = this.bgMat.uniforms;
    bg.uSunB!.value.copy(sun);
    bg.uMoonB!.value.copy(moon);
    bg.uSunI!.value = SUN_E;
    bg.uMoonI!.value = MOON_E;
    bg.uStars!.value = L.night * (1 - overcast);
    bg.uCamHkm!.value = Math.max(0.002, cam.position.y * 0.001);
    bg.uTimeB!.value = input.time;

    r.setRenderTarget(null);
    return L;
  }
}
